import React from 'react';

// Функция для получения публикаций, соответствующих поисковому запросу
export const getPublicationsMatchingSearch = (searchValue, originalPublications) => {
  if (!searchValue || !originalPublications || originalPublications.length === 0) {
    return [];
  }

  const searchTerm = searchValue.toLowerCase().trim();

  return originalPublications.filter((publication) => {
    // Парсим publication_settings для получения данных поиска
    let publicationSettings = {};
    try {
      if (
        publication.publication_settings &&
        typeof publication.publication_settings === 'string'
      ) {
        if (
          publication.publication_settings.startsWith('{') ||
          publication.publication_settings.startsWith('[')
        ) {
          publicationSettings = JSON.parse(publication.publication_settings);
        }
      }
    } catch (error) {
      // Если не удалось распарсить, оставляем пустой объект
    }

    // Поиск по publication_search_words (строка, разделенная запятыми)
    let searchWordsFromSettings = [];
    if (publicationSettings.publication_search_words) {
      if (typeof publicationSettings.publication_search_words === 'string') {
        searchWordsFromSettings = publicationSettings.publication_search_words
          .split(',')
          .map((word) => word.trim().toLowerCase());
      } else if (Array.isArray(publicationSettings.publication_search_words)) {
        searchWordsFromSettings = publicationSettings.publication_search_words.map((word) =>
          word.toLowerCase(),
        );
      }
    }

    // Проверяем совпадение с publication_search_words
    let matchesSearchWords = false;
    if (searchWordsFromSettings.length > 0) {
      matchesSearchWords = searchWordsFromSettings.some((word) => word.includes(searchTerm));
    }

    // Если нет совпадений в publication_search_words, ищем по основным полям публикации
    let matchesMainFields = false;
    if (!matchesSearchWords) {
      const title = publication.title ? publication.title.toLowerCase() : '';
      const description = publication.description ? publication.description.toLowerCase() : '';
      const content = publication.content ? publication.content.toLowerCase() : '';

      // Для названия ищем по отдельным словам
      let matchesTitle = false;
      if (title) {
        const titleWords = title.split(/\s+/);
        matchesTitle = titleWords.some((word) => word.includes(searchTerm));
      }

      matchesMainFields =
        matchesTitle || description.includes(searchTerm) || content.includes(searchTerm);
    }

    return matchesSearchWords || matchesMainFields;
  });
};

// Система отслеживания навигации
export const saveNavigationState = (path, page) => {
  try {
    const navigationHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]');
    const state = { path, page, timestamp: Date.now() };

    // Всегда добавляем новую запись в историю (проверка на дубликаты отключена)
    if (true) {
      // Добавляем новую запись в конец массива (сохраняем всю историю)
      navigationHistory.push(state);

      // Ограничиваем историю до 100 записей (удаляем самые старые)
      if (navigationHistory.length > 100) {
        navigationHistory.splice(0, navigationHistory.length - 100);
      }

      localStorage.setItem('navigationHistory', JSON.stringify(navigationHistory));
    }
  } catch (error) {
    // Игнорируем ошибки
  }
};

export const getNavigationState = (path) => {
  try {
    const navigationHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]');

    // Ищем последнее состояние для текущего пути
    const latestForPath = navigationHistory
      .filter((item) => item.path === path)
      .sort((a, b) => b.timestamp - a.timestamp)[0];

    return latestForPath ? latestForPath.page : 1;
  } catch (error) {
    return 1;
  }
};

export const navigateBack = (currentPaginationPage) => {
  try {
    const navigationHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]');

    if (navigationHistory.length >= 2) {
      // Берем предпоследний элемент из истории
      const previousState = navigationHistory[navigationHistory.length - 2];

      // Добавляем текущее состояние в историю "Вперед" перед переходом
      const forwardHistory = JSON.parse(localStorage.getItem('forwardHistory') || '[]');
      const currentState = {
        path: window.location.pathname,
        page: currentPaginationPage,
        timestamp: Date.now(),
      };
      forwardHistory.push(currentState);
      localStorage.setItem('forwardHistory', JSON.stringify(forwardHistory));

      // Возвращаем данные для перехода
      return {
        path: previousState.path,
        page: previousState.page,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
};

export const navigateForward = () => {
  try {
    const forwardHistory = JSON.parse(localStorage.getItem('forwardHistory') || '[]');

    if (forwardHistory.length > 0) {
      // Берем последний элемент из истории "Вперед"
      const nextState = forwardHistory[forwardHistory.length - 1];

      // Возвращаем данные для перехода БЕЗ изменения истории
      // История будет изменена только при успешном переходе
      return {
        path: nextState.path,
        page: nextState.page,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
};

// Функция для обработки навигации браузера
export const handleBrowserNavigation = (
  navigate,
  dispatch,
  setPaginationPage,
  setIsBrowserNavigation,
  setMobileMenuOpen,
  currentPaginationPage,
) => {
  const handlePopState = (event) => {
    setIsBrowserNavigation(true);
    dispatch(setMobileMenuOpen(false)); // Закрываем мобильный подхедер при навигации браузера

    // Определяем направление навигации
    // Если есть forwardHistory и он не пустой, то это навигация вперед
    const forwardHistory = JSON.parse(localStorage.getItem('forwardHistory') || '[]');
    const isForwardNavigation = forwardHistory.length > 0;

    let navigationResult;
    if (isForwardNavigation) {
      navigationResult = navigateForward();
    } else {
      navigationResult = navigateBack(currentPaginationPage);
    }

    if (navigationResult) {
      // Переходим на нужную страницу
      navigate(navigationResult.path);
      dispatch(setPaginationPage(navigationResult.page));
    }
  };

  return handlePopState;
};

// Функция для обработки смены страницы пагинации
export const handlePageChange = (page, dispatch, setPaginationPage, navigate, location, currentTab) => {
  dispatch(setPaginationPage(page));

  // Формируем новый URL
  const pathArray = location.pathname.split('/').filter((item) => item !== '');
  let newPath;

  if (page === 1) {
    // Для первой страницы убираем номер
    if (pathArray.length === 0) {
      newPath = '/';
    } else if (pathArray.length === 1 && /^\d+$/.test(pathArray[0])) {
      // Убираем /:number для main_tab
      newPath = '/';
    } else if (pathArray.length === 2 && /^\d+$/.test(pathArray[1])) {
      // Убираем /:number для обычных разделов
      newPath = `/${pathArray[0]}`;
    } else {
      newPath = location.pathname;
    }
  } else {
    // Для остальных страниц добавляем номер
    if (pathArray.length === 0 || (pathArray.length === 1 && /^\d+$/.test(pathArray[0]))) {
      // main_tab
      newPath = `/${page}`;
    } else if (pathArray.length === 1) {
      // Обычный раздел без номера
      newPath = `/${pathArray[0]}/${page}`;
    } else if (pathArray.length === 2 && /^\d+$/.test(pathArray[1])) {
      // Обычный раздел с номером - заменяем номер
      newPath = `/${pathArray[0]}/${page}`;
    } else {
      newPath = location.pathname;
    }
  }

  // Переходим на новый URL
  navigate(newPath);

  // Сохраняем состояние навигации
  saveNavigationState(newPath, page);

  // Прокручиваем к началу страницы
  // window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Функция для обработки показа всех публикаций
export const handleShowAllPublications = (dispatch, resetFiltersOnlyWithUpdate) => {
  dispatch(resetFiltersOnlyWithUpdate());
};

// Функция для обработки сброса поиска
export const handleResetSearch = (dispatch, setSearchValueWithUpdate) => {
  dispatch(setSearchValueWithUpdate(''));
};

// Функция для вычисления текущих публикаций с учетом пагинации
export const getCurrentPublications = (publications, currentPaginationPage, itemsPerPage) => {
  if (!publications || publications.length === 0) {
    return [];
  }

  // Если объектов меньше чем itemsPerPage, возвращаем все объекты
  if (publications.length <= itemsPerPage) {
    return publications;
  }

  const startIndex = (currentPaginationPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return publications.slice(startIndex, endIndex);
};

// Функция для вычисления индекса начала страницы
export const getStartIndex = (currentPaginationPage, itemsPerPage) => {
  return (currentPaginationPage - 1) * itemsPerPage;
};

// Функция для проверки, есть ли результаты поиска
export const hasSearchResults = (searchValue, publicationsMatchingSearch) => {
  return searchValue && searchValue.trim() !== '' && Array.isArray(publicationsMatchingSearch) && publicationsMatchingSearch.length > 0;
};

// Функция для получения сообщения о результатах поиска
export const getSearchResultsMessage = (
  searchValue,
  publicationsMatchingSearch,
  publicationsLength,
) => {
  if (!searchValue || searchValue.trim() === '') {
    return null;
  }

  const matchCount = Array.isArray(publicationsMatchingSearch) ? publicationsMatchingSearch.length : 0;

  if (matchCount === 0) {
    return `По запросу "${searchValue}" ничего не найдено`;
  }

  if (matchCount < publicationsLength) {
    return `Найдено ${matchCount} из ${publicationsLength} публикаций по запросу "${searchValue}"`;
  }

  return null;
};
